import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import {
  emptyOdds,
  type BjBonusStatus,
  type KnChatMessage,
  type KnGameUpdate,
  type KnRaceCue,
} from "@ziolo/shared";

const EMPTY_TABLE: KnGameUpdate = {
  phase: "waiting",
  roundNumber: 0,
  players: [],
  bets: [],
  odds: emptyOdds(),
  horseWins: [0, 0, 0, 0, 0, 0],
  lastResults: [],
  finishOrder: null,
  bettingEndsAt: null,
  raceEndsAt: null,
  resultEndsAt: null,
  winners: [],
};

export interface KonieSession extends BjBonusStatus {
  seated: boolean;
}

export function useKonieSocket(enabled: boolean) {
  const socketRef = useRef<Socket | null>(null);
  const [table, setTable] = useState<KnGameUpdate>(EMPTY_TABLE);
  const [session, setSession] = useState<KonieSession | null>(null);
  const [chat, setChat] = useState<KnChatMessage[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletEpoch, setWalletEpoch] = useState(0);
  const [race, setRace] = useState<KnRaceCue | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const socket = io(`${window.location.origin}/konie`, {
      path: "/socket.io/",
      withCredentials: true,
      transports: ["polling", "websocket"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (err: Error) => {
      setConnected(false);
      setError(
        err.message === "UNAUTHORIZED"
          ? "Sesja wygasła. Zaloguj się ponownie."
          : "Nie udało się połączyć z torem. Odśwież stronę.",
      );
    });
    socket.on("game_update", (update: KnGameUpdate) => setTable(update));
    socket.on("session", (next: KonieSession) => setSession(next));
    socket.on("race_start", (cue: KnRaceCue) => setRace(cue));
    socket.on("race_result", () => {
      /* table snapshot carries winners; keep the last cue so the finish pose holds. */
    });
    socket.on("chat:init", (payload: { messages: KnChatMessage[] }) => {
      setChat(payload.messages);
    });
    socket.on("chat:new", (message: KnChatMessage) => {
      setChat((prev) => [...prev, message].slice(-150));
    });
    socket.on("daily:status", (status: BjBonusStatus) => {
      setSession((prev) => ({ seated: prev?.seated ?? false, ...status }));
    });
    socket.on("daily:claimed", (result: { ok: boolean; chips?: number; nextAvailableAt?: number | null }) => {
      if (result.ok) {
        setWalletEpoch((n) => n + 1);
        setSession((prev) => ({
          seated: prev?.seated ?? false,
          canClaim: false,
          nextAvailableAt: result.nextAvailableAt ?? Date.now() + 2 * 60 * 60 * 1000,
          amount: 2137,
          chips: result.chips ?? prev?.chips ?? 0,
          keys: prev?.keys ?? 0,
          equippedSkin: prev?.equippedSkin ?? "classic",
        }));
      }
    });
    socket.on("notice", (payload: { text: string }) => {
      setNotice(payload.text);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  useEffect(() => {
    if (table.phase === "betting" || table.phase === "waiting") setRace(null);
  }, [table.phase, table.roundNumber]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const emit = useCallback((event: string, payload?: unknown) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (payload === undefined) socket.emit(event);
    else socket.emit(event, payload);
  }, []);

  return {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    race,
    join: () => emit("join_game"),
    leave: () => emit("leave_game"),
    placeBet: (kind: string, horses: number[], amount: number) =>
      emit("place_bet", { kind, horses, amount }),
    clearBets: () => emit("clear_bets"),
    sendChat: (text: string) => emit("chat:send", { text, cid: crypto.randomUUID() }),
    claimBonus: () => emit("daily:claim"),
  };
}
